import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApp, apiFetch } from '../../context/AppContext';
import { Card, Badge, DashboardStatCard, ProgressBar, Table } from '../../components/common/UI';
import SmtpSettingsPanel from '../../components/common/SmtpSettingsPanel';
import CollapsibleSettingsCard from '../../components/common/CollapsibleSettingsCard';
import {
  Users, Search, Building2, Package,
  AlertTriangle, CheckCircle, Mail, Settings,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Users Overview
// ─────────────────────────────────────────────────────────────────────────────
export function UsersOverviewPage() {
  const { showNotification } = useApp();

  const [tenants,    setTenants]    = useState([]);
  const [allUsers,   setAllUsers]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [filterSlug, setFilterSlug] = useState('all');

  // 1. Load all tenants first, then fetch users per tenant
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Get all tenants (up to 100)
        const res = await apiFetch('/admin/tenants?page=1&page_size=100');
        if (!res.ok) { showNotification('Failed to load tenants', 'error'); return; }
        const data = await res.json();
        const tenantList = data.items || [];
        if (!cancelled) setTenants(tenantList);

        // Fetch users for every tenant in parallel
        const results = await Promise.allSettled(
          tenantList.map(t =>
            apiFetch(`/admin/tenants/${t.slug}/users`).then(r => r.json()).then(users =>
              (users || []).map(u => ({
                ...u,
                tenantName: t.company_name,
                tenantSlug: t.slug,
                tenantId:   t.id,
              }))
            )
          )
        );

        const merged = results
          .filter(r => r.status === 'fulfilled')
          .flatMap(r => r.value);

        if (!cancelled) setAllUsers(merged);
      } catch {
        if (!cancelled) showNotification('Network error', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showNotification]);

  const filtered = allUsers.filter(u => {
    const matchSearch =
      (u.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.email     || '').toLowerCase().includes(search.toLowerCase());
    const matchTenant = filterSlug === 'all' || u.tenantSlug === filterSlug;
    return matchSearch && matchTenant;
  });

  const columns = [
    {
      key: 'full_name',
      label: 'User',
      render: (v, row) => (
        <div>
          <div className="font-medium text-slate-800">{v}</div>
          <div className="text-xs text-slate-400">{row.email}</div>
        </div>
      ),
    },
    {
      key: 'tenantName',
      label: 'Organization',
      render: v => (
        <div className="flex items-center gap-1.5">
          <Building2 size={13} className="text-slate-300" />
          <span className="text-sm text-slate-600">{v}</span>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: v => (
        <Badge variant={v === 'active' ? 'success' : 'inactive'}>{v}</Badge>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: v => (
        <span className="text-xs text-slate-500">
          {v ? new Date(v).toLocaleDateString() : '—'}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
          <Users size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">User Overview</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Read-only view of all users across tenants
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <DashboardStatCard
          label="Total Users"
          value={allUsers.length}
          icon={Users}
          iconClass="from-indigo-500 to-violet-600"
          loading={loading}
        />
        <DashboardStatCard
          label="Active Users"
          value={allUsers.filter(u => u.status === 'active').length}
          icon={CheckCircle}
          iconClass="from-emerald-400 to-teal-600"
          loading={loading}
        />
        <DashboardStatCard
          label="Organizations"
          value={tenants.length}
          icon={Building2}
          iconClass="from-violet-500 to-purple-600"
          loading={loading}
        />
      </div>

      <Card padding={false}>
        <div className="flex flex-col sm:flex-row gap-3 px-5 py-4 border-b border-slate-100">
          <div className="relative flex-1 max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search users…"
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
            />
          </div>
          <select
            value={filterSlug}
            onChange={e => setFilterSlug(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Organizations</option>
            {tenants.map(t => (
              <option key={t.id} value={t.slug}>{t.company_name}</option>
            ))}
          </select>
        </div>
        <div className="p-4">
          {loading
            ? <p className="text-sm text-slate-400 text-center py-10">Loading users…</p>
            : <Table columns={columns} data={filtered} emptyMessage="No users found" />
          }
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Licenses
// ─────────────────────────────────────────────────────────────────────────────
export function LicensesPage() {
  const { showNotification } = useApp();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await apiFetch('/admin/tenants?page=1&page_size=100');
      const data = await res.json();
      setTenants(data.items || []);
    } catch {
      showNotification('Failed to load license data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const totalLicenses = tenants.reduce((s, t) => s + (t.license?.user_limit    ?? 0), 0);
  const usedSlots     = tenants.reduce((s, t) => s + (t.license?.users_created ?? 0), 0);
  const nearLimit     = tenants.filter(t => {
    const l = t.license;
    return l && l.user_limit && (l.users_created / l.user_limit) > 0.8;
  }).length;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
          <Package size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">License Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">Monitor and manage license allocations</p>
        </div>
      </div>

      <div data-tour-id="tour-licenses-page" className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <DashboardStatCard
          label="Total Licenses"
          value={totalLicenses}
          icon={Package}
          iconClass="from-indigo-500 to-violet-600"
          loading={loading}
        />
        <DashboardStatCard
          label="Used Slots"
          value={usedSlots}
          icon={Users}
          iconClass="from-emerald-400 to-teal-600"
          loading={loading}
        />
        <DashboardStatCard
          label="Near Limit"
          value={nearLimit}
          icon={AlertTriangle}
          iconClass="from-amber-400 to-orange-500"
          loading={loading}
        />
      </div>

      {loading && (
        <p className="text-sm text-slate-400 text-center py-10">Loading licenses…</p>
      )}

      <div data-tour-id="tour-licenses-list" className="space-y-4">
        {tenants.map(ta => {
          const lic       = ta.license;
          if (!lic) return null;
          const used      = lic.users_created  ?? 0;
          const limit     = lic.user_limit     ?? 1;
          const pct       = Math.round((used / limit) * 100);
          const isWarning = pct > 80;
          const isDanger  = pct > 95;

          return (
            <Card key={ta.id}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center text-primary-700 font-bold">
                    {ta.company_name?.[0] ?? '?'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800">{ta.company_name}</span>
                      <Badge variant={ta.status === 'active' ? 'success' : 'inactive'}>
                        {ta.status}
                      </Badge>
                      <Badge variant="primary" size="xs">{lic.plan}</Badge>
                      {isDanger  && <Badge variant="danger"  size="xs">Critical</Badge>}
                      {isWarning && !isDanger && (
                        <Badge variant="warning" size="xs">Near Limit</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Expires: {lic.expires_at}
                    </p>
                  </div>
                </div>

                <div className="md:min-w-[260px]">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">{used} of {limit} users</span>
                    <span
                      className={`font-semibold ${
                        isDanger ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-slate-700'
                      }`}
                    >
                      {pct}%
                    </span>
                  </div>
                  <ProgressBar
                    value={used}
                    max={limit}
                    color={isDanger ? 'red' : isWarning ? 'amber' : 'primary'}
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    {lic.slots_remaining ?? (limit - used)} slots remaining
                  </p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin Settings  (profile only — no editable fields in the backend yet)
// ─────────────────────────────────────────────────────────────────────────────
export function AdminSettingsPage() {
  const { currentUser } = useApp();

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
          <Settings size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="text-sm text-slate-500 mt-0.5">Platform configuration and account</p>
        </div>
      </div>
      <div data-tour-id="tour-admin-settings-profile">
      <Card>
        <h2 className="text-base font-semibold text-slate-800 mb-4">Account Information</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Name</label>
            <p className="text-sm font-medium text-slate-800 mt-1">
              {currentUser?.full_name || currentUser?.name || '—'}
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Email</label>
            <p className="text-sm font-medium text-slate-800 mt-1">{currentUser?.email || '—'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Role</label>
            <p className="text-sm font-medium text-slate-800 mt-1">Super Administrator</p>
          </div>
        </div>
      </Card>
      </div>

      <SmtpSettingsPanel
        apiBase="/admin/settings"
        tourId="tour-smtp-settings"
        title="Platform SMTP"
        description="Default mail server for platform notifications and tenant onboarding emails. Used as fallback when tenant SMTP is not configured."
      />

      <div data-tour-id="tour-admin-email-cards" className="space-y-6">
      {/* Email Template card */}
      <CollapsibleSettingsCard
        title="Email Template Branding"
        description="Customize the logo text, color scheme, copyright, and disclaimer for outgoing system emails."
        icon={<Mail size={18} className="text-white" />}
        iconClassName="bg-gradient-to-br from-rose-500 to-red-600"
        headerRight={
          <Link
            to="/admin/email-templates"
            className="inline-flex items-center justify-center px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow transition"
          >
            Configure
          </Link>
        }
      />

      {/* Email Activity Logs card */}
      <CollapsibleSettingsCard
        title="Email Activities Logs"
        description="View, search, and track all platform-wide outgoing emails, testing alerts, and delivery statuses."
        icon={<Mail size={18} className="text-white" />}
        iconClassName="bg-gradient-to-br from-indigo-500 to-blue-600"
        headerRight={
          <Link
            to="/admin/email-logs"
            className="inline-flex items-center justify-center px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow transition"
          >
            View Logs
          </Link>
        }
      />
      </div>
    </div>
  );
}